import { BarTask } from '@interfaces/ganttTasks/barTask';

import { taskLinkTypesNames } from '@components/ModalComponents/TaskForm/LinkTasksForm/constants';

// The base width of the arrowhead triangle when active
const TRIANGLE_BASE_ACTIVE = 7.5;

// The base width of the arrowhead triangle when inactive
const TRIANGLE_BASE_INACTIVE = 2.5;

// Half of the arrowhead triangle height when active
const TRIANGLE_HEIGHT_HALF_ACTIVE = 4.25;

// Half of the arrowhead triangle height when inactive
const TRIANGLE_HEIGHT_HALF_INACTIVE = 1.5;

interface IDrownPathAndTriangle {
	taskFrom: BarTask;
	taskTo: BarTask;
	rowHeight: number;
	taskHeight: number;
	arrowIndent: number;
	isActive: boolean;
	linkType: string;
}

export const drownPathAndTriangle = ({
	taskFrom,
	taskTo,
	isActive,
	rowHeight,
	arrowIndent,
	taskHeight,
	linkType,
}: IDrownPathAndTriangle) => {
	// Determines the vertical direction of the arrow based on task order (-1 for upward, 1 for downward)
	const indexCompare = taskFrom.index > taskTo.index ? -1 : 1;

	const taskHeightHalf = taskHeight / 2;

	// Y-coordinate of the target task's center
	const taskToYPosition = taskTo.y + taskHeightHalf;

	// Y-coordinate of the source task's center
	const taskFromYPosition = taskFrom.y + taskHeightHalf;

	// The base width of the arrowhead
	const triangleBase = isActive ? TRIANGLE_BASE_ACTIVE : TRIANGLE_BASE_INACTIVE;

	// Half of the arrowhead triangle height
	const triangleHeightHalf = isActive ? TRIANGLE_HEIGHT_HALF_ACTIVE : TRIANGLE_HEIGHT_HALF_INACTIVE;

	// Coordinates for the left-facing arrowhead (points to a task on the left)
	const trianglePointsLeft = `${taskTo.x1},${taskToYPosition}
	${taskTo.x1 - triangleBase},${taskToYPosition - triangleHeightHalf}
	${taskTo.x1 - triangleBase},${taskToYPosition + triangleHeightHalf}`;

	// Coordinates for the right-facing arrowhead (points to a task on the right)
	const trianglePointsRight = `${taskTo.x2},${taskToYPosition}
	${taskTo.x2 + triangleBase},${taskToYPosition - triangleHeightHalf}
	${taskTo.x2 + triangleBase},${taskToYPosition + triangleHeightHalf}`;

	switch (true) {
		case (linkType === taskLinkTypesNames.startToStart): {
			// Indicates whether the start of the taskFrom is before the start of the taskTo
			const isTaskFromBeforeTaskTo = taskFrom.x1 - arrowIndent * 2 < taskTo.x1;

			const taskFromHorizontalOffsetValue = isTaskFromBeforeTaskTo ? '' : `H ${taskTo.x1 - arrowIndent}`;

			const taskToHorizontalOffsetValue = isTaskFromBeforeTaskTo
				? taskTo.x1 - taskFrom.x1 + arrowIndent
				: arrowIndent;

			const startToStartPath = `M ${taskFrom.x1} ${taskFromYPosition}
				h ${-arrowIndent}
				${taskFromHorizontalOffsetValue}
				v ${(indexCompare * rowHeight) / 2}
				V ${taskToYPosition}
				h ${taskToHorizontalOffsetValue}`;

			return [startToStartPath, trianglePointsLeft];
		}

		case (linkType === taskLinkTypesNames.finishToFinish): {
			// Indicates whether the end of the taskFrom is after the end of the taskTo
			const isTaskFromAfterTaskTo = taskFrom.x2 + arrowIndent > taskTo.x2;

			const taskFromHorizontalOffsetValue = isTaskFromAfterTaskTo ? '' : `H ${taskTo.x2 + arrowIndent}`;

			const taskToHorizontalOffsetValue = isTaskFromAfterTaskTo
				? taskTo.x2 - taskFrom.x2 - arrowIndent
				: -arrowIndent;

			const finishToFinishPath = `M ${taskFrom.x2} ${taskFromYPosition}
				h ${arrowIndent}
				${taskFromHorizontalOffsetValue}
				v ${(indexCompare * rowHeight) / 2}
				V ${taskToYPosition}
				h ${taskToHorizontalOffsetValue}`;

			return [finishToFinishPath, trianglePointsRight];
		}

		case (linkType === taskLinkTypesNames.startToFinish): {
			// Indicates whether the start of the taskFrom is after the end of the taskTo
			const isTaskFromAfterTaskTo = taskFrom.x1 - arrowIndent * 2 > taskTo.x2;

			const taskFromHorizontalOffsetValue = isTaskFromAfterTaskTo ? '' : `H ${taskTo.x2 + arrowIndent}`;

			const taskToHorizontalOffsetValue = isTaskFromAfterTaskTo
				? taskTo.x2 - taskFrom.x1 + arrowIndent
				: -arrowIndent;

			const startToFinishPath = `M ${taskFrom.x1} ${taskFromYPosition}
				h ${-arrowIndent}
				v ${(indexCompare * rowHeight) / 2}
				${taskFromHorizontalOffsetValue}
				V ${taskToYPosition}
				h ${taskToHorizontalOffsetValue}`;

			return [startToFinishPath, trianglePointsRight];
		}

		// Default Finish To Start link type
		default: {
			// Indicates whether the end of the taskFrom is before the start of the taskTo
			const isTaskFromBeforeTaskTo = taskFrom.x2 + arrowIndent * 2 < taskTo.x1;

			const taskFromHorizontalOffsetValue = isTaskFromBeforeTaskTo ? '' : `H ${taskTo.x1 - arrowIndent}`;

			const taskToHorizontalOffsetValue = isTaskFromBeforeTaskTo
				? taskTo.x1 - taskFrom.x2 - arrowIndent
				: arrowIndent;

			const finishToStartPath = `M ${taskFrom.x2} ${taskFromYPosition}
				h ${arrowIndent}
				v ${(indexCompare * rowHeight) / 2}
				${taskFromHorizontalOffsetValue}
				V ${taskToYPosition}
				h ${taskToHorizontalOffsetValue}`;

			return [finishToStartPath, trianglePointsLeft];
		}
	}
};
